"use client";

import { BarChart3, ChartLine, ChartPie, List, Plus, SlidersHorizontal } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ChartAppearance,
    ChartBlock,
    ChartPoint,
    ChartVariant,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import {
    chartAppearanceGroups,
    chartPalette,
    chartPointColor,
    chartUsesPointColors,
    clampChartValue,
    createChartPoint,
    resolveChartAppearance,
} from "../../lib/blockRegistry";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

const VARIANTS: { key: ChartVariant; icon: typeof BarChart3; labelKey: string }[] = [
    { key: "bar", icon: BarChart3, labelKey: "lomda-v3-editor.chart.variant-bar" },
    { key: "line", icon: ChartLine, labelKey: "lomda-v3-editor.chart.variant-line" },
    { key: "donut", icon: ChartPie, labelKey: "lomda-v3-editor.chart.variant-donut" },
];

export default function ChartSettings({ block }: { block: ChartBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const { variant, points, unit } = block.data;
    const appearance = resolveChartAppearance(block.data.appearance, theme);

    const update = (patch: Partial<ChartBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });

    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />

            <ToolbarDivider />

            {/* The chart shape — the one setting an author changes most, so it
                sits in the bar itself rather than inside a popover. */}
            <div className="flex shrink-0 items-center gap-1 rounded-lg border border-border p-0.5">
                {VARIANTS.map((v) => {
                    const Icon = v.icon;
                    const on = variant === v.key;
                    return (
                        <button
                            key={v.key}
                            type="button"
                            aria-label={t(v.labelKey)}
                            title={t(v.labelKey)}
                            onClick={() => update({ variant: v.key })}
                            className={cn(
                                "flex size-8 items-center justify-center rounded-md transition",
                                on
                                    ? "bg-accent text-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            <Icon className="size-4" />
                        </button>
                    );
                })}
            </div>

            <AppearancePopover
                variant={variant}
                points={points}
                appearance={appearance}
                onChange={(patch) =>
                    update({ appearance: { ...appearance, ...patch } })
                }
                onPoints={(next) => update({ points: next })}
                onReset={() =>
                    update({
                        appearance: undefined,
                        points: points.map(({ color: _color, ...p }) => p),
                    })
                }
            />
            <DataPopover
                variant={variant}
                points={points}
                unit={unit}
                onPoints={(next) => update({ points: next })}
                onUnit={(u) => update({ unit: u })}
            />
        </div>
    );
}

/** Colours + surface. Bars and slices are coloured per point; a line has one
 *  colour, since it is a single path. */
function AppearancePopover({
    variant,
    points,
    appearance,
    onChange,
    onPoints,
    onReset,
}: {
    variant: ChartVariant;
    points: ChartPoint[];
    appearance: ChartAppearance;
    onChange: (patch: Partial<ChartAppearance>) => void;
    onPoints: (points: ChartPoint[]) => void;
    onReset: () => void;
}) {
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const palette = chartPalette(theme, points.length);
    const groups = chartAppearanceGroups(variant);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.chart.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="max-h-[70vh] w-[320px] overflow-y-auto rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.chart.appearance")}
                    </PopoverTitle>

                    {chartUsesPointColors(variant) && points.length > 0 && (
                        <>
                            <div className="flex flex-col gap-4">
                                {points.map((p, i) => (
                                    <AppearanceRow
                                        key={p.id}
                                        label={
                                            p.label.trim() ||
                                            t(
                                                "lomda-v3-editor.chart.slice-number",
                                                undefined,
                                                { number: i + 1 }
                                            )
                                        }
                                    >
                                        <ColorChipField
                                            value={chartPointColor(
                                                p,
                                                i,
                                                palette
                                            )}
                                            onChange={(c) =>
                                                onPoints(
                                                    points.map((x) =>
                                                        x.id === p.id
                                                            ? { ...x, color: c }
                                                            : x
                                                    )
                                                )
                                            }
                                        />
                                    </AppearanceRow>
                                ))}
                            </div>
                            <AppearanceDivider />
                        </>
                    )}

                    {groups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                const apKey =
                                    field.key as keyof ChartAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "switch" ? (
                                            <Switch
                                                checked={Boolean(value)}
                                                onCheckedChange={(v) =>
                                                    onChange({ [apKey]: v })
                                                }
                                            />
                                        ) : field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}

                    {/* Slice/bar colours live on the points, so the reset up in
                        ChartSettings clears those together with `appearance` —
                        in ONE update, or the second write restores the first. */}
                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}

/** The numbers themselves: one row per point, plus the unit they're shown in. */
function DataPopover({
    variant,
    points,
    unit,
    onPoints,
    onUnit,
}: {
    variant: ChartVariant;
    points: ChartPoint[];
    unit: string;
    onPoints: (points: ChartPoint[]) => void;
    onUnit: (unit: string) => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <List className="size-5" />
                    {t("lomda-v3-editor.chart.manage-data")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="max-h-[70vh] w-[352px] overflow-y-auto rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.chart.manage-data")}
                    </PopoverTitle>

                    {/* A donut is always read as a share of its whole, so it
                        has no unit to choose. */}
                    {variant !== "donut" && (
                        <>
                            <AppearanceRow
                                label={t("lomda-v3-editor.chart.unit")}
                            >
                                <input
                                    value={unit}
                                    onChange={(e) => onUnit(e.target.value)}
                                    placeholder={t(
                                        "lomda-v3-editor.chart.unit-placeholder"
                                    )}
                                    className="h-9 w-[110px] rounded-lg bg-muted px-2.5 text-center text-sm text-foreground outline-none"
                                />
                            </AppearanceRow>
                            <AppearanceDivider />
                        </>
                    )}

                    <div className="flex flex-col gap-2">
                        {points.map((p, i) => (
                            <div key={p.id} className="flex items-center gap-2">
                                <input
                                    value={p.label}
                                    onChange={(e) =>
                                        onPoints(
                                            points.map((x) =>
                                                x.id === p.id
                                                    ? {
                                                          ...x,
                                                          label: e.target.value,
                                                      }
                                                    : x
                                            )
                                        )
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.chart.slice-number",
                                        undefined,
                                        { number: i + 1 }
                                    )}
                                    className="h-9 min-w-0 flex-1 rounded-lg border border-border bg-transparent px-2.5 text-sm text-foreground outline-none"
                                />
                                <input
                                    type="number"
                                    dir="ltr"
                                    min={variant === "donut" ? 0 : undefined}
                                    onChange={(e) =>
                                        onPoints(
                                            points.map((x) =>
                                                x.id === p.id
                                                    ? {
                                                          ...x,
                                                          value: clampChartValue(
                                                              variant,
                                                              Number(
                                                                  e.target.value
                                                              ) || 0
                                                          ),
                                                      }
                                                    : x
                                            )
                                        )
                                    }
                                    value={p.value}
                                    className="h-9 w-20 shrink-0 rounded-lg border border-border bg-transparent px-2 text-center text-sm text-foreground outline-none"
                                />
                                <button
                                    type="button"
                                    aria-label={t("lomda-v3-editor.block.delete")}
                                    disabled={points.length <= 2}
                                    onClick={() =>
                                        onPoints(
                                            points.filter((x) => x.id !== p.id)
                                        )
                                    }
                                    className="shrink-0 text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                                >
                                    <Plus className="size-5 rotate-45" />
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => onPoints([...points, createChartPoint()])}
                        className={cn(chromeBtn, "h-10 self-start")}
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.chart.add")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
